import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SearchForJob from '../components/searchForJob.tsx';
import ContactForm from '../components/ContactForm.tsx';

const Index = () => {
    const [activeTab, setActiveTab] = useState('candidates');
    const [modalMessage, setModalMessage] = useState<string | null>(null);
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const message = params.get('message');
        if (message) {
            // Decode the base64 encoded message
            const decodedMessage = atob(message);
            setModalMessage(decodedMessage);
        }
    }, [location]);

    const closeModal = () => {
        setModalMessage(null);
    };

    return (
        <div className="min-h-screen">
            {/* Hero Section with Background Gradient */}
            <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-32">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-4xl font-bold">Welcome to CVSeeyou</h1>
                        <p className="mt-4">Your AI-powered recruitment platform</p>
                        <SearchForJob />
                        <p className='text-white text-sm mt-4'>
                            Note: By searching, you consent to recruiters contacting you if they have a suitable role.
                            This search includes roles posted within the last 30 days.
                        </p>
                    </div>
                </div>
            </section>

            {/* About Us Section */}
            <section className="about-us py-20">
                <div className="container mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-4">About Us</h2>
                    <p>We bridge the gap between job seekers and recruiters with AI-powered solutions.</p>
                </div>
            </section>

            {/* Reasons to Sign Up */}
            <section className="reasons py-20 bg-gray-100">
                <div className="container mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-4">Why Sign Up?</h2>
                    <div className="flex justify-center mb-8">
                        <button
                            className={`px-4 py-2 mx-2 ${activeTab === 'candidates' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'} rounded`}
                            onClick={() => setActiveTab('candidates')}
                        >
                            Candidates
                        </button>
                        <button
                            className={`px-4 py-2 mx-2 ${activeTab === 'recruiters' ? 'bg-blue-600 text-white' : 'bg-white text-blue-600'} rounded`}
                            onClick={() => setActiveTab('recruiters')}
                        >
                            Recruiters
                        </button>
                    </div>
                    {activeTab === 'candidates' && (
                        <div className="flex flex-wrap justify-center">
                            <div className="w-full md:w-2/3 lg:w-1/2 p-4">
                                <div className="bg-white p-6 rounded-lg shadow-lg text-left">
                                    <h3 className="text-2xl font-bold mb-4">For Candidates</h3>
                                    <ol className="list-decimal list-inside space-y-2">
                                        <li>We allow you to search for roles based on your resume and skills, ensuring higher quality matches and saving time.</li>
                                        <li>If the role is not posted here, you can copy-paste the job description and upload your resume to receive a fit report and improvement suggestions.</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    )}
                    {activeTab === 'recruiters' && (
                        <div className="flex flex-wrap justify-center">
                            <div className="w-full md:w-2/3 lg:w-1/2 p-4">
                                <div className="bg-white p-6 rounded-lg shadow-lg text-left">
                                    <h3 className="text-2xl font-bold mb-4">For Recruiters</h3>
                                    <ol className="list-decimal list-inside space-y-2">
                                        <li>Find candidates in your database based on your clients' job descriptions.</li>
                                        <li>Search your job description database with a candidate's CV to identify potential fits for previous roles, offering business development opportunities.</li>
                                        <li>Eliminate the need to read thousands of resumes; we do it all for you in seconds.</li>
                                        <li>Use our AI assistant to craft the perfect job description with no effort at all, ensuring you attract the best candidates.</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Features Section */}
            <section className="features py-20 bg-gray-100">
                <div className="container mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-4">Key Features</h2>
                    <div className="flex flex-wrap justify-center">
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">AI-Powered Matching</h3>
                                <p>Our AI algorithms ensure you get the best job matches based on your resume and skills.</p>
                            </div>
                        </div>
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">Resume Analysis</h3>
                                <p>Receive detailed reports on how well your resume fits job descriptions and get improvement suggestions.</p>
                            </div>
                        </div>
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">Efficient Hiring</h3>
                                <p>Recruiters can quickly find the best candidates without sifting through thousands of resumes.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works py-20 bg-white">
                <div className="container mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-4">How It Works</h2>
                    <div className="flex flex-wrap justify-center">
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-gray-100 p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">Step 1</h3>
                                <p>Sign up and create your profile.</p>
                            </div>
                        </div>
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-gray-100 p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">Step 2</h3>
                                <p>Upload your resume or job description.</p>
                            </div>
                        </div>
                        <div className="w-full md:w-1/3 p-4">
                            <div className="bg-gray-100 p-6 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold mb-2">Step 3</h3>
                                <p>Get matched with the best jobs or candidates.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Form */}
            <section className="contact py-20">
                <div className="container mx-auto text-center">
                    <ContactForm />
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-blue-600 text-white">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl font-bold mb-4">Get Started Today</h2>
                    <p>Join CVSeeyou and experience the future of recruitment.</p>
                </div>
            </section>

            {/* Modal */}
            {modalMessage && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                        <h2 className="text-2xl font-bold mb-4">Message</h2>
                        <p>{modalMessage}</p>
                        <button
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={closeModal}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Index;
